import './ChatAttachmentChipComponent.css';

export interface ChatAttachmentChipProps {
  id?: string;
  fileName: string;
  mimeType: string;
  size: number;
  status?: 'uploading' | 'ready' | 'error';
  errorMessage?: string;
  downloadUrl?: string;
  onRemove?: () => void;
}

const CODE_FILE_PATTERN = /\.(ts|tsx|js|jsx|py|go|java|rb|rs|sh|sql|css|toml|ini|env|conf|json|jsonl|yaml|yml|xml|html|htm)$/i;
const TEXT_FILE_PATTERN = /\.(txt|md|markdown|log)$/i;

// The extension checks come after the Office MIME checks but are the only reliable
// signal for text files, whose reported MIME type varies by browser and platform.
function fileIconName(mimeType: string, fileName: string): string {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'picture_as_pdf';
  if (mimeType.includes('word') || mimeType.includes('wordprocessingml')) return 'article';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheetml') || mimeType.includes('ms-excel')) return 'table_chart';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'slideshow';
  if (/\.(csv|tsv)$/i.test(fileName)) return 'table_chart';
  if (CODE_FILE_PATTERN.test(fileName)) return 'code';
  if (TEXT_FILE_PATTERN.test(fileName)) return 'description';
  return 'attach_file';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ChatAttachmentChipComponent({
  fileName,
  mimeType,
  size,
  status = 'ready',
  errorMessage,
  downloadUrl,
  onRemove,
}: ChatAttachmentChipProps): JSX.Element {
  const iconName = fileIconName(mimeType, fileName);
  const truncated = fileName.length > 28 ? `${fileName.slice(0, 25)}…` : fileName;

  return (
    <div className={`attachment-chip attachment-chip--${status}`} title={status === 'error' ? (errorMessage ?? 'Upload failed') : fileName}>
      <span className="material-icons attachment-chip__icon">{iconName}</span>
      <span className="attachment-chip__name">{truncated}</span>
      <span className="attachment-chip__size">{formatSize(size)}</span>
      {status === 'uploading' && <span className="attachment-chip__spinner" aria-label="Uploading" />}
      {status === 'ready' && downloadUrl && (
        <a
          href={downloadUrl}
          download={fileName}
          className="attachment-chip__download"
          title="Download"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="material-icons">file_download</span>
        </a>
      )}
      {status === 'error' && (
        <span className="material-icons attachment-chip__error-icon" title={errorMessage}>error_outline</span>
      )}
      {onRemove && (
        <button
          type="button"
          className="attachment-chip__remove"
          onClick={onRemove}
          aria-label={`Remove ${fileName}`}
          title="Remove"
        >
          <span className="material-icons">close</span>
        </button>
      )}
    </div>
  );
}
